import React, { useCallback } from 'react'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'

import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'

import { BrandedButton } from 'src/shared/components/common/button'
import { t } from 'src/translations'

import {
  trackDiningOptionChangedV2,
  TrackingSource
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import { ScreenProps, useNavigateToCheckout } from './DeliveryModal'
import useTracker from 'src/lib/js/hooks/useTracker'

const CheckoutRequirementScreen = ({
  goToScreen,
  closeModal,
  inRangeAddresses,
  outOfRangeAddresses
}: ScreenProps) => {
  const {
    canOrderTakeout,
    setSelectedDiningOptionBehavior,
    updateCartFulfillment,
    fulfillmentData
  } = useFulfillment()
  const navigateToCheckout = useNavigateToCheckout()
  const tracker = useTracker()

  const handleSwitchToPickup = useCallback(async () => {
    if (!canOrderTakeout) return
    setSelectedDiningOptionBehavior(DiningOptionBehavior.TakeOut)
    if (fulfillmentData?.cartFulfillmentData) {
      await updateCartFulfillment({
        fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
        fulfillmentDateTime:
          fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
        diningOptionBehavior: DiningOptionBehavior.TakeOut,
        deliveryInfo: null
      })
      trackDiningOptionChangedV2(
        tracker,
        fulfillmentData.cartFulfillmentData.fulfillmentType,
        false,
        TrackingSource.CheckoutBlockerModal,
        DiningOptionBehavior.TakeOut,
        null
      )
      closeModal()
      navigateToCheckout()
      return
    }
    closeModal()
    navigateToCheckout()
  }, [
    canOrderTakeout,
    setSelectedDiningOptionBehavior,
    fulfillmentData?.cartFulfillmentData,
    closeModal,
    navigateToCheckout,
    updateCartFulfillment,
    tracker
  ])

  const handleAddDeliveryAddress = useCallback(() => {
    const addressSelectionScreenEligible =
      inRangeAddresses.length > 0 || outOfRangeAddresses.length > 0
    const nextScreen = addressSelectionScreenEligible
      ? 'address-hub'
      : 'address-search'
    goToScreen(nextScreen, undefined, TrackingSource.CheckoutBlockerModal)
  }, [inRangeAddresses.length, outOfRangeAddresses.length, goToScreen])

  return (
    <div
      data-testid='checkout-requirement-screen'
      className='checkoutRequirementScreen'
    >
      <div className='requirementScreenHeading'>
        <h2
          id='delivery-address-modal-title'
          tabIndex={-1}
          data-testid='checkout-requirement-title'
        >
          {t('checkout-requirement-screen.title')}
        </h2>
        <div data-testid='checkout-requirement-description'>
          {t(
            canOrderTakeout
              ? 'checkout-requirement-screen.description'
              : 'checkout-requirement-screen.description-no-pickup'
          )}
        </div>
      </div>
      <div className='footer stacked'>
        <BrandedButton
          onClick={closeModal}
          className='cancelButton'
          testId='checkout-requirement-cancel'
        >
          {t('fulfillment-general.cancel')}
        </BrandedButton>
        {canOrderTakeout && (
          <BrandedButton
            onClick={handleSwitchToPickup}
            className='cancelButton'
            testId='checkout-requirement-switch-pickup'
          >
            <span>{t('fulfillment-general.switch-to-pickup')}</span>
          </BrandedButton>
        )}
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={handleAddDeliveryAddress}
          className='optionButton primaryOption'
          testId='checkout-requirement-add-address'
        >
          <span>{t('checkout-requirement-screen.add-delivery-address')}</span>
        </BrandedButton>
      </div>
    </div>
  )
}

export default CheckoutRequirementScreen
